/**
 * Terms of Service Page
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  FamilyRestroom,
  Gavel,
  Security,
  Payment,
  Warning
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function TermsOfService() {
  const navigate = useNavigate();
  useAuth();

  const lastUpdated = "February 8, 2026";

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            sx={{ 
              minWidth: 48, 
              minHeight: 48, 
              borderRadius: 1,
              padding: '12px',
              marginRight: '8px',
              '& .MuiSvgIcon-root': {
                fontSize: '24px'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <FamilyRestroom sx={{ ml: 2, mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Terms of Service
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ 
        py: 4,
        '& .MuiTypography-body1': { fontSize: { xs: '16px', sm: '1rem' }, lineHeight: 1.6 },
        '& .MuiTypography-body2': { fontSize: { xs: '14px', sm: '0.875rem' }, lineHeight: 1.5 },
        '& .MuiTypography-h6': { fontSize: { xs: '1.1rem', sm: '1.2rem' } },
        '& .MuiTypography-h5': { fontSize: { xs: '1.3rem', sm: '1.5rem' } },
      }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
            <Gavel sx={{ mr: 2, verticalAlign: 'middle' }} />
            Terms of Service
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Last updated: {lastUpdated}
          </Typography>

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to Earn-E Chore Helper! These Terms of Service ("Terms") govern your use of our family management 
              application and related services. By accessing or using our app, you agree to be bound by these Terms.
            </Typography>
          </Box>

          {/* Description of Service */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              Earn-E Chore Helper is a family management application that helps families:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Assign and track household chores and tasks</li>
              <li>Manage allowances and rewards for children</li>
              <li>Create and manage shopping lists</li>
              <li>Store family recipes and cooking instructions</li>
              <li>Set and track savings goals</li>
              <li>Integrate with Google Calendar for task scheduling</li>
            </Box>
          </Box>

          {/* User Accounts */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              3. User Accounts and Responsibilities
            </Typography>
            
            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              3.1 Account Creation
            </Typography>
            <Typography variant="body1" paragraph>
              Account requirements:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>You must be at least 18 years old to create an account</li>
              <li>Parents must create accounts for children under 13</li>
              <li>You are responsible for maintaining account security</li>
              <li>You must provide accurate and current information</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              3.2 Parental Responsibilities
            </Typography>
            <Typography variant="body1" paragraph>
              Parents must:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Parents are responsible for all family member accounts</li>
              <li>Parents control data collection and sharing for children</li>
              <li>Parents must provide consent for any third-party integrations</li>
              <li>Parents are responsible for monitoring children's app usage</li>
            </Box>
          </Box>

          {/* Acceptable Use */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              4. Acceptable Use Policy
            </Typography>
            
            <Typography variant="body1" paragraph>
              You agree to use our service only for lawful purposes and in accordance with these Terms. You agree NOT to:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit harmful, offensive, or inappropriate content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt our services</li>
              <li>Use automated systems to access our app</li>
              <li>Share account credentials with others</li>
              <li>Create fake or misleading family profiles</li>
              <li>Use the app for commercial purposes without permission</li>
            </Box>
          </Box>

          {/* Payment Terms */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Payment sx={{ mr: 1 }} />
              5. Payment Terms and Subscriptions
            </Typography>
            
            <Typography variant="body1" paragraph>
              Our app offers both free and premium features:
            </Typography>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.1 Free Features
            </Typography>
            <Typography variant="body1" paragraph>
              Included in free tier:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Basic task management and assignment</li>
              <li>Family member management</li>
              <li>Basic reward tracking</li>
              <li>Limited recipe storage</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.2 Premium Features
            </Typography>
            <Typography variant="body1" paragraph>
              Premium subscription includes:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Advanced AI assistants (Earn-E and Burt)</li>
              <li>Unlimited recipe storage and photo scanning</li>
              <li>Advanced shopping list features</li>
              <li>Google Calendar integration</li>
              <li>Priority customer support</li>
            </Box>

            <Typography variant="h3" component="h3" gutterBottom sx={{ mt: 2 }}>
              5.3 Billing and Cancellation
            </Typography>
            <Typography variant="body1" paragraph>
              Billing details:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Premium subscriptions are billed monthly or annually</li>
              <li>You can cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>No refunds for partial periods, except as required by law</li>
            </Box>
          </Box>

          {/* Privacy and Data */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              6. Privacy and Data Protection
            </Typography>
            
            <Typography variant="body1" paragraph>
              Your privacy is important to us. Please review our Privacy Policy for detailed information about how we collect, 
              use, and protect your information. By using our service, you consent to our data practices as described in our Privacy Policy.
            </Typography>
          </Box>

          {/* Third-Party Services */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              7. Third-Party Services
            </Typography>
            
            <Typography variant="body1" paragraph>
              Our app integrates with third-party services:
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li><strong>Google Calendar:</strong> Optional integration for task scheduling</li>
              <li><strong>Stripe:</strong> Payment processing for premium subscriptions</li>
              <li><strong>Firebase:</strong> Data storage and authentication</li>
              <li><strong>Amazon Associates:</strong> Affiliate links for product suggestions</li>
              <li><strong>Brainstorm AI Kit:</strong> Website analytics (anonymized usage data)</li>
            </Box>

            <Typography variant="body1" paragraph>
              These services have their own terms and privacy policies. We are not responsible for their practices.
            </Typography>
          </Box>

          {/* Intellectual Property */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              8. Intellectual Property Rights
            </Typography>
            
            <Typography variant="body1" paragraph>
              Intellectual property terms:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>The app and its content are owned by Earn-E Chore Helper</li>
              <li>You retain ownership of content you create (recipes, photos, etc.)</li>
              <li>You grant us a license to use your content to provide our services</li>
              <li>You may not copy, modify, or distribute our app without permission</li>
            </Box>
          </Box>

          {/* Disclaimers */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Warning sx={{ mr: 1 }} />
              9. Disclaimers and Limitations
            </Typography>
            
            <Typography variant="body1" paragraph>
              Important disclaimers:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>Our service is provided "as is" without warranties of any kind</li>
              <li>We do not guarantee uninterrupted or error-free service</li>
              <li>We are not responsible for any damages resulting from app usage</li>
              <li>Our liability is limited to the amount you paid for premium features</li>
            </Box>
          </Box>

          {/* Termination */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              10. Termination
            </Typography>
            
            <Typography variant="body1" paragraph>
              Termination terms:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <li>You may terminate your account at any time</li>
              <li>We may suspend or terminate accounts that violate these Terms</li>
              <li>Upon termination, your right to use the service ceases immediately</li>
              <li>We will delete your data within 30 days of account termination</li>
            </Box>
          </Box>

          {/* Changes to Terms */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              11. Changes to Terms
            </Typography>
            
            <Typography variant="body1" paragraph>
              We may update these Terms from time to time. We will notify you of material changes by email or through the app. 
              Continued use of the service after changes constitutes acceptance of the new Terms.
            </Typography>
          </Box>

          {/* Governing Law */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              12. Governing Law
            </Typography>
            
            <Typography variant="body1" paragraph>
              These Terms are governed by the laws of Indiana. Any disputes will be resolved in the courts of United States.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" gutterBottom>
              13. Contact Information
            </Typography>
            
            <Typography variant="body1" paragraph>
              If you have questions about these Terms, please contact us:
            </Typography>

            <Typography variant="body1">
              <strong>Email:</strong> legal@earn-echore.app<br />
              <strong>Address:</strong> Earn-E Chore Helper Legal Team<br />
              <strong>Response Time:</strong> We will respond within 7 business days
            </Typography>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body2" align="center">
              <strong>These Terms of Service are effective as of {lastUpdated}</strong>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default TermsOfService;











